import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import AppImage from '../../../components/AppImage';

const SocialSection = ({ friends, activities, photos, onNavigate }) => {
  const [activeSection, setActiveSection] = useState('friends');

  const sections = [
    { id: 'friends', label: 'Amigos', icon: 'Users' },
    { id: 'activity', label: 'Atividade', icon: 'Activity' },
    { id: 'photos', label: 'Fotos', icon: 'Image' }
  ];

  const getActivityIcon = (type) => {
    switch (type) {
      case 'match_result': return 'Trophy';
      case 'achievement': return 'Award';
      case 'friend_added': return 'UserPlus';
      case 'match_created': return 'Plus';
      case 'barbecue_planned': return 'Utensils';
      default: return 'Bell';
    }
  };

  const getActivityColor = (type) => {
    switch (type) {
      case 'match_result': return 'bg-success/10 text-success';
      case 'achievement': return 'bg-warning/10 text-warning';
      case 'friend_added': return 'bg-primary/10 text-primary';
      case 'match_created': return 'bg-secondary/10 text-secondary';
      case 'barbecue_planned': return 'bg-accent/10 text-accent';
      default: return 'bg-muted/50 text-muted-foreground';
    }
  };

  const formatTimeAgo = (date) => {
    const now = new Date();
    const diff = Math.floor((now - new Date(date)) / 1000);
    
    if (diff < 60) return 'agora mesmo';
    if (diff < 3600) return `${Math.floor(diff / 60)}min atrás`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h atrás`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d atrás`;
    return new Date(date)?.toLocaleDateString('pt-BR');
  };

  const getOnlineStatus = (status) => {
    return status === 'online' ? 'bg-success' : 'bg-muted';
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-elevation-2 overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <h2 className="font-heading font-bold text-lg text-card-foreground mb-3">
          Social
        </h2>
        
        {/* Section Tabs */}
        <div className="flex space-x-1 bg-muted rounded-lg p-1">
          {sections?.map((section) => (
            <button
              key={section?.id}
              onClick={() => setActiveSection(section?.id)}
              className={`flex items-center space-x-2 px-3 py-1 text-sm font-medium rounded transition-standard flex-1 justify-center ${
                activeSection === section?.id
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name={section?.icon} size={16} />
              <span>{section?.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="max-h-80 overflow-y-auto">
        {/* Friends Section */}
        {activeSection === 'friends' && (
          <div className="p-4">
            {friends?.length > 0 ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm font-medium text-card-foreground">
                    {friends?.length} amigos
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="UserPlus"
                    iconPosition="left"
                    iconSize={14}
                    className="text-primary"
                  >
                    Adicionar
                  </Button>
                </div>
                
                {friends?.map((friend) => (
                  <div key={friend?.id} className="flex items-center space-x-3 p-2 rounded-lg hover:bg-muted/30 transition-standard">
                    <div className="relative">
                      <AppImage
                        src={friend?.avatar}
                        alt={friend?.avatarAlt}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                      <div className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 border-2 border-white rounded-full ${getOnlineStatus(friend?.status)}`}></div>
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-card-foreground text-sm">{friend?.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {friend?.mutualMatches} peladas juntos
                      </p>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {friend?.status === 'online' ? 'Online' : formatTimeAgo(friend?.lastSeen)}
                    </div>
                  </div>
                ))}
                
                <div className="pt-2 border-t border-border">
                  <Button
                    variant="ghost"
                    fullWidth
                    size="sm"
                    className="text-primary hover:text-primary"
                  >
                    Ver todos os amigos
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-center py-6">
                <div className="flex items-center justify-center w-12 h-12 bg-muted/50 rounded-lg mx-auto mb-3">
                  <Icon name="Users" size={20} className="text-muted-foreground" />
                </div>
                <p className="text-sm text-muted-foreground">
                  Nenhum amigo adicionado ainda
                </p>
                <Button
                  variant="default"
                  size="sm"
                  className="mt-3"
                  iconName="UserPlus"
                  iconPosition="left"
                  iconSize={16}
                >
                  Adicionar Amigos
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Activity Section */}
        {activeSection === 'activity' && (
          <div className="p-4">
            {activities?.length > 0 ? (
              <div className="space-y-4">
                <div className="text-sm font-medium text-card-foreground mb-4">
                  Atividade recente
                </div>
                
                {activities?.map((activity) => (
                  <div key={activity?.id} className="flex items-start space-x-3">
                    <div className={`p-2 rounded-lg flex-shrink-0 ${getActivityColor(activity?.type)}`}>
                      <Icon name={getActivityIcon(activity?.type)} size={16} />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-card-foreground text-sm">
                        {activity?.title}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {activity?.description}
                      </p>
                      
                      {/* Activity metadata */}
                      {activity?.metadata && (
                        <div className="flex items-center space-x-3 mt-2 text-xs text-muted-foreground">
                          {activity?.metadata?.goals && (
                            <div className="flex items-center space-x-1">
                              <Icon name="Target" size={12} />
                              <span>{activity?.metadata?.goals} gols</span>
                            </div>
                          )}
                          {activity?.metadata?.venue && (
                            <div className="flex items-center space-x-1">
                              <Icon name="MapPin" size={12} />
                              <span>{activity?.metadata?.venue}</span>
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Participants */}
                      {activity?.participants && (
                        <div className="flex items-center space-x-1 mt-2">
                          {activity?.participants?.slice(0, 3)?.map((participant, index) => (
                            <AppImage
                              key={index}
                              src={participant?.avatar}
                              alt={participant?.avatarAlt}
                              className="w-6 h-6 rounded-full object-cover border border-white"
                            />
                          ))}
                          {activity?.participants?.length > 3 && (
                            <div className="w-6 h-6 rounded-full bg-muted text-xs flex items-center justify-center text-muted-foreground">
                              +{activity?.participants?.length - 3}
                            </div>
                          )}
                        </div>
                      )}
                      
                      <div className="text-xs text-muted-foreground mt-2">
                        {formatTimeAgo(activity?.timestamp)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6">
                <div className="flex items-center justify-center w-12 h-12 bg-muted/50 rounded-lg mx-auto mb-3">
                  <Icon name="Activity" size={20} className="text-muted-foreground" />
                </div>
                <p className="text-sm text-muted-foreground">
                  Nenhuma atividade recente
                </p>
              </div>
            )}
          </div>
        )}

        {/* Photos Section */}
        {activeSection === 'photos' && (
          <div className="p-4">
            {photos?.length > 0 ? (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm font-medium text-card-foreground">
                    {photos?.length} fotos
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Upload"
                    iconPosition="left"
                    iconSize={14}
                    className="text-primary"
                  >
                    Upload
                  </Button>
                </div>
                
                <div className="grid grid-cols-3 gap-2">
                  {photos?.slice(0, 9)?.map((photo) => (
                    <div key={photo?.id} className="relative group cursor-pointer">
                      <AppImage
                        src={photo?.url}
                        alt={photo?.alt}
                        className="w-full aspect-square rounded-lg object-cover"
                      />
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                        <Icon name="Expand" size={16} className="text-white" />
                      </div>
                      <div className="absolute bottom-1 left-1 text-xs text-white bg-black/50 px-1 rounded">
                        {new Date(photo?.date)?.toLocaleDateString('pt-BR')}
                      </div>
                    </div>
                  ))}
                </div>
                
                {photos?.length > 9 && (
                  <div className="pt-3 border-t border-border mt-3">
                    <Button
                      variant="ghost"
                      fullWidth
                      size="sm"
                      className="text-primary hover:text-primary"
                    >
                      Ver todas as fotos ({photos?.length})
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-6">
                <div className="flex items-center justify-center w-12 h-12 bg-muted/50 rounded-lg mx-auto mb-3">
                  <Icon name="Image" size={20} className="text-muted-foreground" />
                </div>
                <p className="text-sm text-muted-foreground mb-3">
                  Nenhuma foto compartilhada ainda
                </p>
                <Button
                  variant="default"
                  size="sm"
                  iconName="Upload"
                  iconPosition="left"
                  iconSize={16}
                >
                  Fazer Upload
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SocialSection;